import { useState } from 'react'

import { useGetBrokersNeedsAttentionItemStatistics } from 'components/hooks/needs_attention/useGetBrokersNeedsAttentionItemStatistics'
import { type NAVv2Filters } from 'components/hooks/useGetBrokersMissingPayments'
import AnimateOpacity from 'components/reusable/AnimateOpacity'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Switch } from 'components/ui/switch'
import { Banner } from 'slab'

import {
  NAVCarrierNamePieChart,
  NAVCarrierNameTable,
} from './NAVCarrierNameStatistics'
import {
  NAVEnrollmentTypePieChart,
  NAVEnrollmentTypeTable,
} from './NAVEnrollmentTypeStatistics'
import { NAVReasonPieChart, NAVReasonTable } from './NAVReasonStatistics'
import { NAVStatusPieChart, NAVStatusTable } from './NAVStatusStatistics'

type NeedsAttentionStatisticsProps = {
  filters: NAVv2Filters
}

const statisticsConfig = {
  by_state: { count: NAVStatusTable, percent: NAVStatusPieChart },
  by_enrollment_type: {
    count: NAVEnrollmentTypeTable,
    percent: NAVEnrollmentTypePieChart,
  },
  by_reason: { count: NAVReasonTable, percent: NAVReasonPieChart },
  by_carrier_name: {
    count: NAVCarrierNameTable,
    percent: NAVCarrierNamePieChart,
  },
}

const NeedsAttentionStatistics = ({
  filters,
}: NeedsAttentionStatisticsProps) => {
  const {
    data: statistics,
    isLoading,
    error,
  } = useGetBrokersNeedsAttentionItemStatistics({ filters })
  const [view, setView] = useState<'count' | 'percent'>('count')

  if (isLoading) {
    return <LoadingSpinnerCentered />
  }

  if (error || !statistics) {
    return (
      <Banner type='critical' title='Unable to get Needs Attention statistics'>
        {error?.message}
      </Banner>
    )
  }

  const totalItems = Object.values(statistics?.by_state || {}).reduce(
    (acc, curr) => acc + curr,
    0
  )

  return (
    <>
      <div className='flex items-center justify-center mt-4'>
        <div className='text-2xl'>{totalItems} Total NAV Items</div>
      </div>
      <div className='flex items-center justify-end'>
        <div className='font-light italic mr-2'>Display %</div>
        <Switch
          checked={view === 'percent'}
          onCheckedChange={(value) => setView(value ? 'percent' : 'count')}
        />
      </div>

      {totalItems > 0
        ? (
          <AnimateOpacity key={view} isOpen>
            <div className='flex flex-wrap gap-1 my-4'>
              {(
                [
                  'by_state',
                  'by_enrollment_type',
                  'by_reason',
                  'by_carrier_name',
                ] as const
              ).map((key) => {
                const componentType = key as keyof typeof statisticsConfig
                const Component = statisticsConfig[componentType][view]

                return (
                  <div key={key} className='flex-1 min-w-[500px] p-4'>
                    <Component stats={statistics[componentType]} />
                  </div>
                )
              })}
            </div>
          </AnimateOpacity>
          )
        : (
          <div className='flex justify-center text-lg font-semibold'>
            No results to display
          </div>
          )}
    </>
  )
}

export default NeedsAttentionStatistics
